// ===========================================
// BUSTED! BLACKJACK - MAIN APP COMPONENT
// ===========================================

import { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import { useAuthStore, useSocketStore } from './stores';
import { AdminRoute, BahtSymbol, ChipPurchaseModal, ConnectionStatus, Currency, ProtectedRoute, TosAcceptanceModal, UserMenu, LiveCasinoStats } from './components';
import { DiagnosticOverlay } from './components/DiagnosticOverlay';
import { AdminPanelPage, CardWipeDemo, CheckoutCancelPage, CheckoutSuccessPage, ControlPanelColorDemo, GuidePage, LandingPage, LoginPage, WelcomePage, LobbyPage, StorePage, LockerPage, Demo2Page, Demo3Page, ModalDemoPage, MusicDemoPage, FontDemoPage, SurveyPage } from './pages';
import CanvasPrototypePage from './pages/CanvasPrototypePage';
import PixiTablePage from './pages/PixiTablePage';
import DemoEventsPage from './pages/DemoEventsPage';
import { useHealthCheck } from './hooks/useHealthCheck';
import TwentyOneV1Page from './pages/TwentyOneV1Page';
import ScorecardDemoPage from './pages/ScorecardDemoPage';
import { api } from './api';
import { ECONOMY_CONSTANTS } from '@busted-blackjack/shared';
import { formatBalance } from './utils/format';
import spinningCoinAnimation from './assets/spinning-coin.json';
import moneyBagAnimation from './assets/money-bag.json';
import bustedLogo from './assets/busted.png';
import loadingVideo from './assets/loading1.mp4';

// ===========================================
// ANIMATED DOTS COMPONENT
// ===========================================

// Animated dots for "reconnecting" text
function AnimatedDots() {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="inline-block w-[12px] text-left">
      {'.'.repeat(dotCount)}
    </span>
  );
}

// ===========================================
// LOADING SCREEN COMPONENT
// ===========================================
// Displays loading video with landscape orientation enforcement
// for small viewport portrait devices (< 700px smaller dimension)

const SMALL_VIEWPORT_THRESHOLD = 700;

interface LoadingScreenProps {
  randomStartTime: number;
}

function LoadingScreen({ randomStartTime }: LoadingScreenProps) {
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine if we need landscape rotation - ALWAYS rotate on portrait
  const isPortrait = viewport.height > viewport.width;
  const needsRotation = isPortrait;

  // Virtual dimensions after rotation
  const effectiveWidth = needsRotation ? viewport.height : viewport.width;
  const effectiveHeight = needsRotation ? viewport.width : viewport.height;

  if (needsRotation) {
    // Rotated layout for portrait small viewports
    return (
      <div className="fixed inset-0 bg-black overflow-hidden">
        <div
          style={{
            position: 'absolute',
            width: effectiveWidth,
            height: effectiveHeight,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            transformOrigin: 'center center',
          }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            onLoadedMetadata={(e) => {
              e.currentTarget.currentTime = randomStartTime;
            }}
          >
            <source src={loadingVideo} type="video/mp4" />
          </video>
        </div>
      </div>
    );
  }

  // Normal layout - video fills viewport width, clips top/bottom
  return (
    <div className="fixed inset-0 bg-black overflow-hidden flex items-center justify-center">
      <video
        autoPlay
        muted
        loop
        playsInline
        className="w-full h-auto min-h-full object-cover"
        onLoadedMetadata={(e) => {
          e.currentTarget.currentTime = randomStartTime;
        }}
      >
        <source src={loadingVideo} type="video/mp4" />
      </video>
    </div>
  );
}

// ===========================================
// ANALYTICS HELPERS
// ===========================================

// Get or create a persistent visitor ID (survives browser restarts)
function getVisitorId(): string {
  const key = 'cg_visitor_id';
  let visitorId = localStorage.getItem(key);
  if (!visitorId) {
    visitorId = crypto.randomUUID();
    localStorage.setItem(key, visitorId);
  }
  return visitorId;
}

// Get or create a session ID (cleared when tab closes)
function getSessionId(): string {
  const key = 'cg_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

// Get device/browser info for analytics
function getDeviceInfo() {
  const ua = navigator.userAgent;

  // Parse device type
  let deviceType = 'desktop';
  if (/Mobile|Android|iPhone|iPad|iPod/i.test(ua)) {
    deviceType = /iPad|Tablet/i.test(ua) ? 'tablet' : 'mobile';
  }

  // Parse browser
  let browser = 'unknown';
  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/')) browser = 'Safari';
  else if (ua.includes('Opera/') || ua.includes('OPR/')) browser = 'Opera';

  // Parse OS
  let os = 'unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return {
    userAgent: ua,
    deviceType,
    browser,
    os,
    screenResolution: `${screen.width}x${screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
  };
}

// Get UTM parameters from URL
function getUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
  };
}

// ===========================================
// FORMATTING HELPERS (matching StatsPanel)
// ===========================================

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString('en-US');
}

function formatChips(num: number): string {
  const prefix = num >= 0 ? '' : '-';
  const absNum = Math.abs(num);
  return prefix + formatNumber(absNum);
}

function formatPercent(num: number): string {
  return num.toFixed(1) + '%';
}

function getCountryFlag(countryCode: string | null): string {
  if (!countryCode || countryCode.length !== 2) {
    return '';
  }
  // Convert country code to regional indicator symbols (flag emoji)
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
}

// Common country code to name mapping
const countryNames: Record<string, string> = {
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  AU: 'Australia',
  DE: 'Germany',
  FR: 'France',
  JP: 'Japan',
  CN: 'China',
  IN: 'India',
  BR: 'Brazil',
  MX: 'Mexico',
  ES: 'Spain',
  IT: 'Italy',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  RU: 'Russia',
  KR: 'South Korea',
  SG: 'Singapore',
  NZ: 'New Zealand',
  IE: 'Ireland',
  CH: 'Switzerland',
  AT: 'Austria',
  BE: 'Belgium',
  PT: 'Portugal',
  ZA: 'South Africa',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PH: 'Philippines',
  TH: 'Thailand',
  MY: 'Malaysia',
  ID: 'Indonesia',
  VN: 'Vietnam',
  TR: 'Turkey',
  UA: 'Ukraine',
  CZ: 'Czech Republic',
  GR: 'Greece',
  HU: 'Hungary',
  RO: 'Romania',
  IL: 'Israel',
  AE: 'UAE',
  SA: 'Saudi Arabia',
  EG: 'Egypt',
  NG: 'Nigeria',
  KE: 'Kenya',
  PK: 'Pakistan',
  BD: 'Bangladesh',
};

function getCountryName(countryCode: string | null): string {
  if (!countryCode) return 'Unknown';
  return countryNames[countryCode.toUpperCase()] || countryCode.toUpperCase();
}

interface StatRowProps {
  label: string;
  value: React.ReactNode;
  highlight?: boolean;
}

function StatRow({ label, value, highlight = false }: StatRowProps) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-gray-400">{label}</span>
      <span className={highlight ? 'text-yellow-400 font-medium' : 'text-white'}>
        {value}
      </span>
    </div>
  );
}

// Home/Dashboard component
function HomePage() {
  const { user, stats, fetchUser, fetchStats } = useAuthStore();
  const { isConnected, isReconnecting, reconnectAttempt, connectionError, forceReconnect } = useSocketStore();
  const navigate = useNavigate();
  const [claimingDaily, setClaimingDaily] = useState(false);

  // Health check polling - auto-reconnect when server becomes available
  useHealthCheck({
    enabled: !isConnected,
    onHealthy: () => console.log('[HomePage] Server healthy, reconnecting...'),
  });
  const [dailyChipsModal, setDailyChipsModal] = useState<{ show: boolean; amount: number; newBalance: number } | null>(null);
  const [showGreedyModal, setShowGreedyModal] = useState(false);
  const [showAlreadyClaimedModal, setShowAlreadyClaimedModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showDailyChoiceModal, setShowDailyChoiceModal] = useState(false);
  const [dailyBargainLoading, setDailyBargainLoading] = useState(false);

  // Find Player state
  const [playerSearch, setPlayerSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    found: boolean;
    players: Array<{
      id: string;
      username: string;
      discriminator: string;
      displayName: string;
      avatarUrl: string | null;
      tableSlug: string | null;
      isOnline: boolean;
      currentTableId: string | null;
    }>;
  } | null>(null);

  // All-time casino stats for the welcome card
  const [allTimeStats, setAllTimeStats] = useState<{
    totalHands: number;
    totalWagered: number;
    totalPlayers: number;
  } | null>(null);

  // Refresh user data and stats when returning to this page
  useEffect(() => {
    fetchUser();
    fetchStats();
    // Fetch all-time stats
    api.analytics.getLiveStats().then(result => {
      if (result.success && result.data) {
        setAllTimeStats({
          totalHands: result.data.totalHands,
          totalWagered: result.data.totalWagered,
          totalPlayers: result.data.totalPlayers,
        });
      }
    });
  }, [fetchUser, fetchStats]);

  const handleClaimDaily = async () => {
    // Check if user is a millionaire - show greedy modal instead
    if ((user?.balance ?? 0) >= ECONOMY_CONSTANTS.CHIP_SOFT_CAP) {
      setShowGreedyModal(true);
      return;
    }

    // Show choice modal with free chips vs daily bargain
    setShowDailyChoiceModal(true);
  };

  const handleClaimFreeChips = async () => {
    setShowDailyChoiceModal(false);
    setClaimingDaily(true);

    try {
      const result = await api.users.claimDailyChips();
      if (result.success && result.data) {
        // Show the modal popup with claimed chips
        setDailyChipsModal({
          show: true,
          amount: result.data.amount,
          newBalance: result.data.newBalance
        });
        // Refresh user data to update balance
        fetchUser();
      } else {
        // Show sarcastic "already claimed" modal
        setShowAlreadyClaimedModal(true);
      }
    } catch (err) {
      // Show sarcastic "already claimed" modal for any error
      setShowAlreadyClaimedModal(true);
    } finally {
      setClaimingDaily(false);
    }
  };

  const handleDailyBargain = async () => {
    setDailyBargainLoading(true);
    try {
      const result = await api.payments.createCheckout('daily-bargain');
      if (result.success && result.data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.data.url;
      } else {
        // Failed - maybe already claimed or other error
        setShowDailyChoiceModal(false);
        alert(result.error?.message || 'Failed to create checkout session');
      }
    } catch (err) {
      setShowDailyChoiceModal(false);
      alert('Failed to create checkout session');
    } finally {
      setDailyBargainLoading(false);
    }
  };

  const handlePlayerSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!playerSearch.trim() || playerSearch.trim().length < 2) return;

    setSearchLoading(true);
    setSearchResults(null);
    try {
      const result = await api.users.searchPlayer(playerSearch.trim());
      if (result.success && result.data) {
        setSearchResults(result.data);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleJoinPlayer = (tableId: string | null, tableSlug: string | null) => {
    // Prefer currentTableId if online at a table, otherwise use their personal table
    if (tableId) {
      navigate(`/table/${tableId}`);
    } else if (tableSlug) {
      navigate(`/table/${tableSlug}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Daily Chips Claim Modal */}
      {dailyChipsModal?.show && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md mx-4 text-center">
            <div className="text-5xl mb-4">🎁</div>
            <h2 className="text-2xl font-bold text-white mb-6">Daily Bonus Claimed!</h2>

            <div className="bg-casino-gold-500/10 border border-casino-gold-500/30 rounded-lg p-6 mb-6">
              <p className="text-casino-gold-400 text-sm mb-1">You've received</p>
              <div className="flex items-center justify-center gap-2">
                <p className="text-4xl font-bold text-casino-gold-400">
                  {dailyChipsModal.amount.toLocaleString('en-US')}
                </p>
                <Lottie
                  animationData={spinningCoinAnimation}
                  loop={true}
                  className="w-12 h-12"
                />
              </div>
              <p className="text-casino-gold-400 text-sm mt-1">daily bucks!</p>
            </div>

            <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
              <p className="text-gray-400 text-sm">New Balance</p>
              <p className="text-2xl font-bold text-white">
                {formatBalance(dailyChipsModal.newBalance)}
              </p>
              <p className="text-gray-500 text-xs mt-1">(Play Money Only)</p>
            </div>

            <button
              onClick={() => setDailyChipsModal(null)}
              className="w-full py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 font-bold rounded-xl transition-colors"
            >
              Awesome!
            </button>
          </div>
        </div>
      )}

      {/* Greedy Millionaire Modal */}
      {showGreedyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md mx-4 text-center">
            <Lottie
              animationData={moneyBagAnimation}
              loop={true}
              className="w-32 h-32 mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-white mb-4">Don't Be Greedy!</h2>
            <p className="text-gray-400 mb-6">
              No free chips for millionaires!
              <br />
              You've got over {formatBalance(ECONOMY_CONSTANTS.CHIP_SOFT_CAP)} chips already.
            </p>
            <button
              onClick={() => setShowGreedyModal(false)}
              className="w-full py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-gray-900 font-bold rounded-xl transition-colors"
            >
              Fine, I'll Play!
            </button>
          </div>
        </div>
      )}

      {/* Already Claimed Modal - Cheeky sarcastic version */}
      {showAlreadyClaimedModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md mx-4 text-center">
            {/* Big Red X */}
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-red-500/20 border-4 border-red-500 flex items-center justify-center">
              <span className="text-6xl font-bold text-red-500">✕</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Nice Try, Buddy!</h2>
            <p className="text-gray-400 mb-2">
              You already grabbed your daily bucks today.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              What, you think we're made of money over here?
              <br />
              Come back tomorrow... if you can wait that long.
            </p>
            <button
              onClick={() => setShowAlreadyClaimedModal(false)}
              className="w-full py-3 bg-red-500 hover:bg-red-400 text-white font-bold rounded-xl transition-colors"
            >
              Ugh, Fine!
            </button>
          </div>
        </div>
      )}

      {/* Buy B-Bucks Modal */}
      {showBuyModal && (
        <ChipPurchaseModal onClose={() => setShowBuyModal(false)} />
      )}

      {/* Daily Choice Modal - Free Chips vs Daily Bargain */}
      {showDailyChoiceModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() => !dailyBargainLoading && setShowDailyChoiceModal(false)}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-lg w-full mx-4 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-5xl mb-4">🎁</div>
            <h2 className="text-2xl font-bold text-white mb-2">Daily Bonus!</h2>
            <p className="text-gray-400 mb-6">Choose your reward <span className="text-gray-500">(Play Money Only)</span>:</p>

            <div className="space-y-4">
              {/* Free Chips Option */}
              <button
                onClick={handleClaimFreeChips}
                disabled={dailyBargainLoading}
                className="w-full py-4 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-xl transition-colors border-2 border-gray-600"
              >
                <div className="text-casino-gold-400 text-2xl font-bold"><BahtSymbol />1,000</div>
                <div className="text-green-400 font-semibold">FREE</div>
              </button>

              {/* OR Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-600"></div>
                <span className="text-gray-400 font-semibold text-sm">OR</span>
                <div className="flex-1 h-px bg-gray-600"></div>
              </div>

              {/* Daily Bargain Option */}
              <button
                onClick={handleDailyBargain}
                disabled={dailyBargainLoading}
                className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:opacity-50 rounded-xl transition-colors border-2 border-amber-400"
              >
                {dailyBargainLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                    <span className="text-gray-900 font-bold">Loading...</span>
                  </div>
                ) : (
                  <>
                    <div className="text-gray-900 text-2xl font-bold"><BahtSymbol />2,500</div>
                    <div className="text-gray-800 font-semibold">Just $1 <span className="text-green-700">(+150% bonus!)</span></div>
                  </>
                )}
              </button>
            </div>

            <button
              onClick={() => setShowDailyChoiceModal(false)}
              disabled={dailyBargainLoading}
              className="mt-4 text-gray-400 hover:text-white text-sm disabled:opacity-50"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={bustedLogo} alt="BUSTED!" className="h-[70px]" />
              <div>
                <h1 className="text-xl font-bold text-white">Busted Blackjack</h1>
                <p className="text-sm text-gray-400">Casino Accurate Gameplay</p>
              </div>
            </div>

            <UserMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Welcome Card */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h2 className="text-lg font-semibold">👋 Welcome back, {user?.username}!</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-700/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-400">Busted Bucks Balance</p>
                  <p className="text-3xl font-bold text-casino-gold-400">
                    <BahtSymbol />{formatBalance(user?.balance ?? 0)}
                  </p>
                  <button
                    onClick={() => setShowBuyModal(true)}
                    className="mt-3 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold rounded-lg transition-colors"
                  >
                    Buy B-Bucks
                  </button>
                </div>

                <div className="bg-gray-700/50 rounded-lg p-4">
                  <p className="text-sm text-casino-gold-400 mb-2 text-center">BUSTED! Blackjack lifetime metrics:</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Hands</span>
                      <span className="font-mono font-semibold text-white">
                        {allTimeStats?.totalHands?.toLocaleString() ?? '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Wagered</span>
                      <span className="font-mono font-semibold text-casino-gold-400">
                        <BahtSymbol />{allTimeStats?.totalWagered?.toLocaleString() ?? '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Players</span>
                      <span className="font-mono font-semibold text-white">
                        {allTimeStats?.totalPlayers?.toLocaleString() ?? '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Daily Chips Button */}
              <div className="mt-4">
                <button
                  onClick={handleClaimDaily}
                  disabled={claimingDaily}
                  className="w-full py-3 bg-casino-gold-500 hover:bg-casino-gold-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-bold rounded-xl transition-colors"
                >
                  {claimingDaily ? 'Claiming...' : '🎁 Claim Daily Bucks'}
                </button>
              </div>

              {/* Play Now Button */}
              <div className="mt-4">
                {connectionError && !isConnected && !isReconnecting ? (
                  <div className="space-y-2">
                    <div className="text-red-400 text-sm text-center">
                      Connection failed: {connectionError}
                    </div>
                    <button
                      onClick={forceReconnect}
                      className="w-full py-4 bg-yellow-600 hover:bg-yellow-500 text-white text-xl font-bold rounded-xl transition-colors"
                    >
                      🔄 Retry Connection
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate('/lobby')}
                    disabled={!isConnected}
                    className={`w-full py-4 ${isConnected ? 'bg-green-600 hover:bg-green-500' : isReconnecting ? 'bg-yellow-600' : 'bg-gray-600'} disabled:cursor-not-allowed text-white text-xl font-bold rounded-xl transition-colors`}
                  >
                    {isConnected
                      ? '🃏 Play Blackjack'
                      : isReconnecting
                        ? <>🔄 Reconnecting{reconnectAttempt > 0 ? ` (${reconnectAttempt})` : ''}<AnimatedDots /></>
                        : '⏳ Connecting...'}
                  </button>
                )}
              </div>

              {/* Store & Locker Buttons - Hidden for MVP */}
              {/* <div className="mt-4 flex gap-3">
                <button
                  onClick={() => navigate('/store')}
                  className="flex-1 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors"
                >
                  🛒 Store
                </button>
                <button
                  onClick={() => navigate('/locker')}
                  className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-colors"
                >
                  📦 My Locker
                </button>
              </div> */}
            </div>
          </div>

          {/* Connection Status */}
          <ConnectionStatus />

          {/* User Info */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">👤 Your Profile</h2>
            </div>
            <div className="card-body space-y-3">
              <div className="flex items-center gap-3">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.displayName}
                    className="w-12 h-12 rounded-full"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-casino-gold-500 flex items-center justify-center text-gray-900 font-bold text-xl">
                    {user?.username.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <p className="font-medium text-white">{user?.displayName}</p>
                  {user?.countryCode && (
                    <img
                      src={`https://flagcdn.com/w40/${user.countryCode.toLowerCase()}.png`}
                      alt={getCountryName(user.countryCode)}
                      title={getCountryName(user.countryCode)}
                      className="h-4 rounded-sm shadow-sm"
                    />
                  )}
                </div>
              </div>

              <div className="border-t border-gray-700 pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Authentication</span>
                  <span className="text-white capitalize">{user?.provider}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">IP Address</span>
                  <span className="text-white font-mono text-xs">
                    {user?.ipAddress || <span className="text-gray-500">Unknown</span>}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Country</span>
                  <span className="text-white">
                    {user?.countryCode ? (
                      <>{getCountryFlag(user.countryCode)} {getCountryName(user.countryCode)}</>
                    ) : (
                      <span className="text-gray-500">Unknown</span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Your Table</span>
                  <span
                    onClick={() => navigate(`/table/${user?.tableSlug}`)}
                    className="text-casino-gold-400 hover:text-casino-gold-300 cursor-pointer font-mono text-xs"
                  >
                    bustedblackjack.com/table/{user?.tableSlug}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Member Since</span>
                  <span className="text-white">
                    {new Date(user?.createdAt || '').toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Player Lifetime Stats */}
              <div className="border-t border-gray-700 pt-3">
                <p className="text-sm font-semibold text-gray-300 mb-3">Player Lifetime</p>
                <div className="space-y-1 text-sm">
                  <StatRow label="Hands Played" value={formatNumber(stats?.handsPlayed ?? 0)} />
                  <StatRow label="Win Rate" value={formatPercent(stats?.winRate ?? 0)} />
                  <StatRow label="Blackjacks" value={stats?.blackjacks ?? 0} />
                  <StatRow label="Best Streak" value={(stats?.bestStreak ?? 0) + ' wins'} highlight />
                  <StatRow label="Worst Streak" value={(stats?.worstStreak ?? 0) + ' losses'} />
                  <StatRow
                    label="Net Result"
                    value={
                      <Currency
                        amount={stats?.netResult ?? 0}
                        showSign
                        positiveClass="text-green-400"
                        negativeClass="text-red-400"
                      />
                    }
                  />
                  <StatRow label="Doubles Won" value={stats?.doublesWon ?? 0} />
                  <StatRow label="Splits Played" value={stats?.splitsPlayed ?? 0} />
                </div>
              </div>
            </div>
          </div>

          {/* Live Casino Stats */}
          <LiveCasinoStats />

          {/* Find Player Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">🔍 Find a Player</h2>
            </div>
            <div className="card-body">
              <form onSubmit={handlePlayerSearch} className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    placeholder="Enter username..."
                    className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-casino-gold-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={searchLoading || playerSearch.trim().length < 2}
                    className="px-4 py-2 bg-casino-gold-500 hover:bg-casino-gold-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-semibold rounded-lg transition-colors"
                  >
                    {searchLoading ? '...' : 'Find'}
                  </button>
                </div>
              </form>

              {/* Search Results */}
              {searchResults && (
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {searchResults.found && searchResults.players.length > 0 ? (
                    searchResults.players.map((player) => (
                      <div key={player.id} className="p-3 bg-gray-700/50 rounded-lg flex items-center gap-3">
                        {player.avatarUrl ? (
                          <img
                            src={player.avatarUrl}
                            alt={player.displayName}
                            className="w-10 h-10 rounded-full flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-casino-gold-500 flex items-center justify-center text-gray-900 font-bold flex-shrink-0">
                            {player.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">{player.displayName}</p>
                          <p className="text-xs text-gray-400">
                            {player.isOnline ? (
                              <span className="text-green-400">● Online</span>
                            ) : (
                              <span className="text-gray-500">○ Offline</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => handleJoinPlayer(player.currentTableId, player.tableSlug)}
                          className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold rounded-lg transition-colors flex-shrink-0"
                        >
                          {player.isOnline && player.currentTableId ? 'Join' : 'Visit'}
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 bg-gray-700/50 rounded-lg">
                      <p className="text-gray-400 text-center">
                        No players found matching "{playerSearch}"
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          BUSTED! Blackjack • <span className="text-gray-400">*</span> Play Money Only • Not Real Gambling • Must be 18+
        </div>
      </footer>
    </div>
  );
}

// Main App with Router
function App() {
  const { user, fetchUser, isLoading, isAuthenticated } = useAuthStore();
  const { connect, disconnect, wasKickedFromOtherLocation, clearKickedFlag } = useSocketStore();
  const didInit = useRef(false);
  const [showTosModal, setShowTosModal] = useState(false);
  const [minDurationElapsed, setMinDurationElapsed] = useState(false);

  // Random start time for loading video (0 to 4 seconds in 0.1s intervals)
  const randomStartTime = useRef(Math.floor(Math.random() * 41) / 10);
  // Random minimum duration (0.5 to 2 seconds in 0.1s intervals)
  const minDuration = useRef((Math.floor(Math.random() * 16) + 5) / 10);

  // Analytics tracking refs
  const loadingStartTime = useRef(Date.now());
  const authCompleteTime = useRef<number | null>(null);
  const analyticsTracked = useRef(false);

  // Fetch user once on mount and start minimum duration timer
  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      fetchUser();
      // Start minimum duration timer
      setTimeout(() => {
        setMinDurationElapsed(true);
      }, minDuration.current * 1000);
    }
  }, []);

  // Track when auth completes
  useEffect(() => {
    if (!isLoading && authCompleteTime.current === null) {
      authCompleteTime.current = Date.now();
    }
  }, [isLoading]);

  // Track loading screen analytics when loading completes
  useEffect(() => {
    if (!isLoading && minDurationElapsed && !analyticsTracked.current) {
      analyticsTracked.current = true;

      const actualDuration = Date.now() - loadingStartTime.current;
      const authLoadTime = authCompleteTime.current
        ? authCompleteTime.current - loadingStartTime.current
        : null;

      const deviceInfo = getDeviceInfo();

      const visitorId = getVisitorId();
      const sessionId = getSessionId();
      const utmParams = getUtmParams();

      // Fire and forget - don't await
      api.analytics.trackLoadingScreen({
        userId: user?.id || null,
        username: user?.username || null,
        targetPage: window.location.pathname,
        previousPage: document.referrer || null,
        actualDuration,
        authLoadTime,
        minDurationUsed: Math.round(minDuration.current * 1000),
        userAgent: deviceInfo.userAgent,
        screenResolution: deviceInfo.screenResolution,
        viewportSize: deviceInfo.viewportSize,
        language: deviceInfo.language,
        visitorId,
        sessionId,
        isAuthenticated,
      }).catch(() => {
        // Silently fail - analytics shouldn't break the app
      });

      // Also track visitor and session
      api.analytics.track({
        visitorId,
        sessionId,
        userId: user?.id || null,
        username: user?.username || null,
        referrer: document.referrer || null,
        landingPage: window.location.pathname,
        utmSource: utmParams.utmSource,
        utmMedium: utmParams.utmMedium,
        utmCampaign: utmParams.utmCampaign,
        userAgent: deviceInfo.userAgent,
        deviceType: deviceInfo.deviceType,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        screenResolution: deviceInfo.screenResolution,
        viewportSize: deviceInfo.viewportSize,
        language: deviceInfo.language,
        isAuthenticated,
      }).catch(() => {
        // Silently fail - analytics shouldn't break the app
      });
    }
  }, [isLoading, minDurationElapsed, user, isAuthenticated]);

  // Check if TOS needs to be accepted
  useEffect(() => {
    if (isAuthenticated && user && !user.tosAcceptedAt) {
      setShowTosModal(true);
    } else {
      setShowTosModal(false);
    }
  }, [isAuthenticated, user]);

  // Connect/disconnect socket based on auth
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [isAuthenticated]);

  // Show loading video until BOTH auth completes AND minimum duration elapses
  if (isLoading || !minDurationElapsed) {
    return <LoadingScreen randomStartTime={randomStartTime.current} />;
  }

  const handleRefreshAfterKick = () => {
    clearKickedFlag();
    window.location.reload();
  };

  return (
    <BrowserRouter>
      {/* TOS Acceptance Modal */}
      {showTosModal && (
        <TosAcceptanceModal onAccepted={() => setShowTosModal(false)} />
      )}

      {/* Diagnostic Overlay - Toggle with Ctrl+Shift+D */}
      <DiagnosticOverlay />

      {/* Connected Elsewhere Modal - shows on ANY page when kicked */}
      {wasKickedFromOtherLocation && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="bg-black/90 backdrop-blur-sm border-2 border-casino-gold-500 rounded-xl p-6 text-center shadow-2xl max-w-sm mx-4">
            <h2 className="text-2xl font-bold text-casino-gold-400 mb-3">
              CONNECTED ELSEWHERE
            </h2>

            <p className="text-gray-300 text-sm mb-2">
              Your session was moved to another browser tab.
            </p>

            <p className="text-gray-400 text-xs mb-4">
              Only one active session per account is allowed.
            </p>

            <button
              onClick={handleRefreshAfterKick}
              className="bg-casino-gold-500 hover:bg-casino-gold-600 text-black font-bold py-2 px-6 rounded-lg transition-colors"
            >
              Refresh to Reconnect
            </button>
          </div>
        </div>
      )}

      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/survey" element={<SurveyPage />} />

        {/* Admin-Only Routes */}
        <Route path="/demo2" element={
          <AdminRoute>
            <Demo2Page />
          </AdminRoute>
        } />
        <Route path="/demo3" element={
          <AdminRoute>
            <Demo3Page />
          </AdminRoute>
        } />
        <Route path="/musicdemo" element={
          <AdminRoute>
            <MusicDemoPage />
          </AdminRoute>
        } />
        <Route path="/fontdemo" element={
          <AdminRoute>
            <FontDemoPage />
          </AdminRoute>
        } />
        <Route path="/21v1" element={
          <AdminRoute>
            <TwentyOneV1Page />
          </AdminRoute>
        } />
        <Route path="/color-demo" element={
          <AdminRoute>
            <ControlPanelColorDemo />
          </AdminRoute>
        } />
        <Route path="/modal-demo" element={
          <AdminRoute>
            <ModalDemoPage />
          </AdminRoute>
        } />
        <Route path="/canvas-prototype" element={
          <AdminRoute>
            <CanvasPrototypePage />
          </AdminRoute>
        } />
        <Route path="/card-wipe-demo" element={
          <AdminRoute>
            <CardWipeDemo />
          </AdminRoute>
        } />
        <Route path="/demo-events" element={
          <AdminRoute>
            <DemoEventsPage />
          </AdminRoute>
        } />
        <Route path="/scorecard-demo" element={
          <AdminRoute>
            <ScorecardDemoPage />
          </AdminRoute>
        } />
        <Route path="/adminpanel" element={
          <AdminRoute>
            <AdminPanelPage />
          </AdminRoute>
        } />

        {/* Protected Routes */}
        <Route path="/welcome" element={
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        } />

        <Route path="/lobby" element={
          <ProtectedRoute>
            <LobbyPage />
          </ProtectedRoute>
        } />

        {/* Cosmetics Store */}
        <Route path="/store" element={
          <ProtectedRoute>
            <StorePage />
          </ProtectedRoute>
        } />

        {/* Cosmetics Locker */}
        <Route path="/locker" element={
          <ProtectedRoute>
            <LockerPage />
          </ProtectedRoute>
        } />

        {/* Checkout pages (Stripe payment flow) */}
        <Route path="/checkout/success" element={
          <ProtectedRoute>
            <CheckoutSuccessPage />
          </ProtectedRoute>
        } />
        <Route path="/checkout/cancel" element={
          <ProtectedRoute>
            <CheckoutCancelPage />
          </ProtectedRoute>
        } />

        {/* Main table page - NOW USING PIXI */}
        <Route path="/table/:tableId" element={
          <ProtectedRoute>
            <PixiTablePage />
          </ProtectedRoute>
        } />


        {/* Home page - authenticated users */}
        <Route path="/home" element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        } />

        {/* Landing page - public marketing page */}
        <Route path="/" element={<LandingPage />} />

        {/* Catch all - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
